import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
    AsYouType,
    getCountries,
    getCountryCallingCode,
    parsePhoneNumberFromString,
} from 'libphonenumber-js';

const API_BASE_URL = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/$/, '');
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const isFlagEnabled = (value) => {
    const flag = (value || '').trim().toLowerCase();
    return flag === 'true' || flag === '1' || flag === 'yes';
};

const isPlatformEnabledFallback = (platform) => {
    const flag = process.env[`REACT_APP_BETA_SIGNUPS_${platform.toUpperCase()}_ENABLED`];
    return flag === undefined || flag === '' || isFlagEnabled(flag);
};

const PLATFORM_OPTIONS = [
    {
        value: 'ios',
        title: 'iOS',
        note: 'TestFlight access',
    },
    {
        value: 'android',
        title: 'Android',
        note: 'Play Store beta channel',
    },
];

const countryNames =
    typeof Intl !== 'undefined' && Intl.DisplayNames
        ? new Intl.DisplayNames(['en'], { type: 'region' })
        : null;

const COUNTRY_OPTIONS = getCountries().map((iso) => ({
    iso,
    name: countryNames?.of(iso) || iso,
    dialCode: `+${getCountryCallingCode(iso)}`,
})).sort((a, b) => {
    if (a.iso === 'US') return -1;
    if (b.iso === 'US') return 1;
    return a.name.localeCompare(b.name);
});

const getFlagEmoji = (iso) =>
    iso
        .toUpperCase()
        .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const formatPhoneInput = (value, countryIso) => new AsYouType(countryIso).input(value);

const getParsedPhoneNumber = (phoneNumber, country) =>
    parsePhoneNumberFromString(phoneNumber.trim(), country.iso);

const pushBetaSignupEvent = ({ signupId, platform }) => {
    if (typeof window === 'undefined' || !window.dataLayer || !signupId) return;

    window.dataLayer.push({
        event: 'beta_signup_submitted',
        beta_signup_id: signupId,
        beta_signup_platform: platform,
    });
};

const getPhoneValidationMessage = (phoneNumber, country) => {
    if (!phoneNumber.trim()) return 'Enter a phone number.';

    const parsedPhoneNumber = getParsedPhoneNumber(phoneNumber, country);
    if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
        return `Enter a valid ${country.name} phone number.`;
    }

    if (parsedPhoneNumber.country && parsedPhoneNumber.country !== country.iso) {
        return `Select ${parsedPhoneNumber.country} or enter a ${country.name} phone number.`;
    }

    return '';
};

const Home = () => {
    const currentYear = new Date().getFullYear();
    const [signupsStatus, setSignupsStatus] = useState({ ios: true, android: true });
    const [formData, setFormData] = useState({ email: '', phone_number: '', platform: '', honeypot: '' });
    const [selectedCountry, setSelectedCountry] = useState(COUNTRY_OPTIONS[0]);
    const [countrySearch, setCountrySearch] = useState('');
    const [countryMenuOpen, setCountryMenuOpen] = useState(false);
    const [status, setStatus] = useState({ type: 'idle', message: '', link: null });
    const [loading, setLoading] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [fingerprint, setFingerprint] = useState({ hash: '', components: {} });
    const [startTime] = useState(() => Date.now());
    const widgetRef = useRef(null);
    const widgetIdRef = useRef(null);
    const countryPickerRef = useRef(null);

    const platformOptions = PLATFORM_OPTIONS.map((platform) => ({
        ...platform,
        enabled: platform.value === 'ios' ? signupsStatus.ios : signupsStatus.android,
    }));
    const hasEnabledPlatforms = platformOptions.some((platform) => platform.enabled);
    const formDisabled = !hasEnabledPlatforms;

    const filteredCountries = COUNTRY_OPTIONS.filter((country) => {
        const query = countrySearch.trim().toLowerCase();
        if (!query) return true;
        return (
            country.name.toLowerCase().includes(query) ||
            country.iso.toLowerCase().includes(query) ||
            country.dialCode.includes(query)
        );
    });

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/beta/signups-status`).then((res) => {
            setSignupsStatus({
                ios: res.data.ios_signups_enabled ?? isPlatformEnabledFallback('ios'),
                android: res.data.android_signups_enabled ?? isPlatformEnabledFallback('android'),
            });
        }).catch(() => {
            setSignupsStatus({
                ios: isPlatformEnabledFallback('ios'),
                android: isPlatformEnabledFallback('android'),
            });
        });
    }, []);

    useEffect(() => {
        const captureFingerprint = async () => {
            try {
                const components = {
                    ua: navigator.userAgent || '',
                    language: navigator.language || '',
                    platform: navigator.platform || '',
                    timezoneOffset: new Date().getTimezoneOffset(),
                    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
                    colorDepth: window.screen?.colorDepth || 0,
                    hardwareConcurrency: navigator.hardwareConcurrency || 0,
                };
                const serialized = JSON.stringify(components);
                const encoder = new TextEncoder();
                const data = encoder.encode(serialized);

                let hashHex = '';
                if (window.crypto?.subtle?.digest) {
                    const digest = await window.crypto.subtle.digest('SHA-256', data);
                    hashHex = Array.from(new Uint8Array(digest))
                        .map((b) => b.toString(16).padStart(2, '0'))
                        .join('');
                } else {
                    hashHex = btoa(serialized);
                }

                setFingerprint({ hash: hashHex, components });
            } catch (err) {
                console.error('Failed to capture fingerprint', err);
            }
        };

        captureFingerprint();
    }, []);

    useEffect(() => {
        if (!TURNSTILE_SITE_KEY || typeof window === 'undefined') return;
        let cancelled = false;

        const renderWidget = () => {
            if (cancelled || !widgetRef.current || widgetIdRef.current) return;

            if (window.turnstile && typeof window.turnstile.render === 'function') {
                widgetIdRef.current = window.turnstile.render(widgetRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    theme: 'dark',
                    action: 'beta_signup',
                    callback: (token) => setTurnstileToken(token),
                    'error-callback': () =>
                        setStatus({ type: 'error', message: 'Verification failed. Please retry.' }),
                    'expired-callback': () => setTurnstileToken(''),
                });
                return;
            }

            setTimeout(renderWidget, 120);
        };

        renderWidget();

        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile?.remove) {
                window.turnstile.remove(widgetIdRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (!countryPickerRef.current || countryPickerRef.current.contains(event.target)) return;
            setCountryMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, []);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handlePhoneChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            phone_number: formatPhoneInput(value, selectedCountry.iso),
        }));
    };

    const selectCountry = (country) => {
        setSelectedCountry(country);
        setFormData((prev) => ({
            ...prev,
            phone_number: formatPhoneInput(prev.phone_number, country.iso),
        }));
        setCountrySearch('');
        setCountryMenuOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formDisabled) return;
        if (!formData.email || !formData.platform) return;
        if (!platformOptions.some((platform) => platform.value === formData.platform && platform.enabled)) {
            setStatus({ type: 'error', message: 'That beta platform is not accepting signups right now.' });
            return;
        }
        if (formData.honeypot) {
            setStatus({ type: 'error', message: 'Bot detection triggered.' });
            return;
        }

        const phoneValidationMessage = getPhoneValidationMessage(formData.phone_number, selectedCountry);
        if (phoneValidationMessage) {
            setStatus({ type: 'error', message: phoneValidationMessage });
            return;
        }

        const elapsedMs = Date.now() - startTime;
        if (elapsedMs < 1200) {
            setStatus({ type: 'error', message: 'Slow down and try again.' });
            return;
        }

        if (TURNSTILE_SITE_KEY && !turnstileToken) {
            setStatus({ type: 'error', message: 'Complete the verification to continue.' });
            return;
        }

        setLoading(true);
        setStatus({ type: 'idle', message: '' });

        try {
            const parsedPhoneNumber = getParsedPhoneNumber(formData.phone_number, selectedCountry);
            const payload = {
                email: formData.email,
                platform: formData.platform,
                phone_number: parsedPhoneNumber.number,
                turnstileToken: turnstileToken || 'missing',
                fingerprint: fingerprint.hash || 'missing',
                fingerprint_components: fingerprint.components,
                elapsed_ms: elapsedMs,
                honeypot: formData.honeypot || '',
            };

            const response = await axios.post(`${API_BASE_URL || ''}/api/beta-signups`, payload);
            const { message, link, id } = response.data;
            pushBetaSignupEvent({ signupId: id, platform: payload.platform });

            setStatus({
                type: 'success',
                message: message || 'You are on the list. We will reach out with your invite soon.',
                link: link || null,
            });
            setFormData({ email: '', phone_number: '', platform: '', honeypot: '' });
            setTurnstileToken('');
            if (widgetIdRef.current && window.turnstile?.reset) {
                window.turnstile.reset(widgetIdRef.current);
            }
        } catch (error) {
            console.error('Unable to save beta tester request', error);
            setStatus({
                type: 'error',
                message: 'We could not save your request right now. Please try again in a moment.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="home">
            <div className="home-card">
                <div className="brand-row">
                    <div className="brand-mark">
                        <img
                            src={`${process.env.PUBLIC_URL || ''}/happtrak-logo.svg`}
                            alt="HappTrak logo"
                            className="brand-logo"
                        />
                    </div>
                    <div className="brand-copy">
                        <div className="brand-name">HappTrak.ai</div>
                    </div>
                </div>

                <div className="hero-layout">
                    <div className="hero-copy">
                        <h1 className="hero-title">Ignore your WhatsApp groups without missing a thing.</h1>
                        <p className="hero-subtitle">
                            Get the signal without the scroll. HappTrak keeps track of your WhatsApp groups so you get the information you want to know:
                        </p>
                        <ul className="hero-list">
                            <li>Extract events from WhatsApp groups. Search and view them on a calendar by month, week, and day.</li>
                            <li>Get push notifications for topics you define—an issue you're passionate about, a relevant job post, apartment listing, and more.
                            </li>
                            <li>Get recaps/summaries of long group conversations to cut down on noise.</li>
                        </ul>
                    </div>

                    <div
                        className={`form-card ${formDisabled ? 'form-card-disabled' : ''}`}
                        id="beta-form"
                    >
                        {formDisabled && (
                            <div className="form-overlay" aria-live="polite">
                                <div className="form-overlay-pill">Beta access opening soon</div>
                            </div>
                        )}
                        <div className="form-header">
                            <div>
                                <div className="form-title">Join the beta test for early access</div>
                               {/* <p className="form-body">Share your details so we can send the right build.</p>*/}
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="beta-form">
                            <label className="field">
                                <span className="field-label">Email Address</span>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    required
                                    disabled={formDisabled}
                                />
                                <span className="field-note">Android users: please use the email address associated with your Google Play account.</span>
                            </label>

                            <div className="field">
                                <label className="field-label" htmlFor="phone">
                                    Phone number
                                </label>
                                <div className="phone-input-row">
                                    <div className="country-picker" ref={countryPickerRef}>
                                        <button
                                            type="button"
                                            className="country-button"
                                            onClick={() => setCountryMenuOpen((open) => !open)}
                                            disabled={formDisabled}
                                            aria-label="Select country code"
                                            aria-expanded={countryMenuOpen}
                                        >
                                            <span className="country-flag" aria-hidden="true">
                                                {getFlagEmoji(selectedCountry.iso)}
                                            </span>
                                            <span>{selectedCountry.dialCode}</span>
                                            <span className="country-caret" aria-hidden="true">⌄</span>
                                        </button>
                                        {countryMenuOpen && (
                                            <div className="country-menu">
                                                <input
                                                    type="search"
                                                    className="country-search"
                                                    placeholder="Search country"
                                                    value={countrySearch}
                                                    onChange={(e) => setCountrySearch(e.target.value)}
                                                    autoFocus
                                                />
                                                <div className="country-list" role="listbox">
                                                    {filteredCountries.map((country) => (
                                                        <button
                                                            type="button"
                                                            key={country.iso}
                                                            className="country-option"
                                                            onClick={() => selectCountry(country)}
                                                            role="option"
                                                            aria-selected={country.iso === selectedCountry.iso}
                                                        >
                                                            <span className="country-flag" aria-hidden="true">
                                                                {getFlagEmoji(country.iso)}
                                                            </span>
                                                            <span className="country-option-name">{country.name}</span>
                                                            <span className="country-option-code">{country.dialCode}</span>
                                                        </button>
                                                    ))}
                                                    {filteredCountries.length === 0 && (
                                                        <div className="country-empty">No countries found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone_number}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        disabled={formDisabled}
                                        required
                                        inputMode="tel"
                                        aria-label="Phone number"
                                    />
                                </div>
                                <small className="field-note">
                                    Used for 2FA to verify WhatsApp account.
                                </small>
                            </div>

                            <div className="field">
                                <span className="field-label">Where will you test?</span>
                                <div className="platform-options">
                                    {platformOptions.map((platform) => (
                                        <label
                                            key={platform.value}
                                            className={`platform-tile ${
                                                formData.platform === platform.value ? 'platform-active' : ''
                                            } ${platform.enabled ? '' : 'platform-disabled'}`}
                                        >
                                            <input
                                                type="radio"
                                                name="platform"
                                                value={platform.value}
                                                required
                                                checked={formData.platform === platform.value}
                                                onChange={(e) => handleChange('platform', e.target.value)}
                                                disabled={!platform.enabled}
                                            />
                                            <span className="platform-title">{platform.title}</span>
                                            <span className="platform-note">
                                                {platform.enabled ? platform.note : 'Opening soon'}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="honeypot">
                                <label>
                                    <span>Leave this field empty</span>
                                    <input
                                        type="text"
                                        name="website"
                                        value={formData.honeypot}
                                        onChange={(e) => handleChange('honeypot', e.target.value)}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        disabled={formDisabled}
                                    />
                                </label>
                            </div>

                            {TURNSTILE_SITE_KEY && !formDisabled ? (
                                <div className="turnstile-wrap">
                                    <div ref={widgetRef} className="turnstile-placeholder" />
                                </div>
                            ) : null}

                            <button
                                type="submit"
                                className="cta-button"
                                disabled={loading || formDisabled}
                            >
                                {loading ? 'Adding you...' : 'Add me to the beta list'}
                            </button>

                            {status.type !== 'idle' && (
                                <div className={`form-status ${status.type}`}>
                                    {status.message}
                                    {status.link && (
                                        <>
                                            {' '}
                                            <a href={status.link} target="_blank" rel="noopener noreferrer">
                                                {status.link}
                                            </a>
                                        </>
                                    )}
                                </div>
                            )}

                            <p className="form-footnote">
                                We only use your email/phone to coordinate access to HappTrak. No spam, ever.
                            </p>
                        </form>
                    </div>
                </div>

                <footer className="site-footer">
                    <p className="site-footer-line">© {currentYear} HappTrak, LLC. All rights reserved.</p>
                    <div className="site-footer-links">
                        <a className="site-footer-link" href="/privacy">
                            Privacy policy
                        </a>
                        <a className="site-footer-link" href="/delete-account">
                            Request account deletion
                        </a>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default Home;
